import { fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import type {
  BaseQueryFn,
  FetchArgs,
  FetchBaseQueryError,
  FetchBaseQueryMeta,
} from '@reduxjs/toolkit/query/react';

import { env } from '@/shared/config/env';
import {
  clearTokens,
  getAccessToken,
  getAsicCompanyId,
  getRefreshToken,
  getUserId,
  setTokens,
} from '@/shared/lib/tokenStorage';

/**
 * The auth middleware.
 *
 * Two responsibilities, matching the request and response interceptors this
 * flow was ported from:
 *
 *   1. Attach the access token to every outgoing request.
 *   2. On a 401, refresh once and replay the original request. If the refresh
 *      fails, drop the session — the tokens are dead and holding them only
 *      produces a redirect loop.
 *
 * Every feature's endpoints run through here because there is one baseApi.
 */

const rawBaseQuery = fetchBaseQuery({
  baseUrl: env.VITE_API_BASE_URL,
  prepareHeaders: (headers) => {
    const accessToken = getAccessToken();
    if (accessToken) {
      // The API expects the bare token, with no `Bearer ` prefix.
      headers.set('authorization', accessToken);
    }
    headers.set('accept', 'application/json');
    return headers;
  },
});

/** Set when the session ends unrecoverably, so the app can react from anywhere
 *  without this module importing the router or the store. */
export const SESSION_EXPIRED_EVENT = 'auth:session-expired';

const endSession = (): void => {
  clearTokens();
  window.dispatchEvent(new Event(SESSION_EXPIRED_EVENT));
};

/**
 * In-flight refresh, shared by every 401 that lands while it runs.
 *
 * Without this, a screen firing four queries at once on an expired token sends
 * four refreshes; the first rotates the refresh token and the other three fail
 * against the now-stale one, logging the director out mid-session. waive-dp had
 * exactly this bug — its `_retry` flag was per-request, so it deduplicated
 * retries of one request but never concurrent requests.
 */
let refreshInFlight: Promise<boolean> | null = null;

const refreshSession = async (
  api: Parameters<BaseQueryFn>[1],
  extraOptions: Parameters<BaseQueryFn>[2],
): Promise<boolean> => {
  const refreshToken = getRefreshToken();
  if (!refreshToken) return false;

  const result = await rawBaseQuery(
    {
      url: '/auth/refresh',
      method: 'PUT',
      body: {},
      headers: { authorization: refreshToken },
    },
    api,
    extraOptions,
  );

  if (result.error) return false;

  const parsed = refreshResponseGuard(result.data);
  if (!parsed) return false;

  // Storage is rewritten wholesale by `setTokens`, so every field it owns has
  // to be carried across. Reading them back first is what stops a refresh from
  // blanking the subject that every director endpoint is keyed on, and from
  // dropping the company id entirely.
  const userId = getUserId();
  if (userId === null) return false;

  setTokens({
    accessToken: parsed.accessToken,
    refreshToken: parsed.refreshToken || refreshToken,
    userId,
    asicCompanyId: getAsicCompanyId() ?? undefined,
  });

  return true;
};

/** Kept local and hand-rolled rather than importing the Zod schema: this module
 *  sits under `shared/`, and reaching into a feature for a type would invert
 *  the dependency direction the architecture depends on. */
const refreshResponseGuard = (
  data: unknown,
): { accessToken: string; refreshToken: string } | null => {
  if (typeof data !== 'object' || data === null) return null;
  const candidate = data as Record<string, unknown>;
  const { accessToken, refreshToken } = candidate;
  if (typeof accessToken !== 'string' || accessToken.length === 0) return null;
  return {
    accessToken,
    refreshToken: typeof refreshToken === 'string' ? refreshToken : '',
  };
};

export const baseQueryWithReauth: BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError,
  object,
  FetchBaseQueryMeta
> = async (args, api, extraOptions) => {
  const result = await rawBaseQuery(args, api, extraOptions);

  if (result.error?.status !== 401) return result;

  // The refresh call itself 401ing means the refresh token is spent.
  const url = typeof args === 'string' ? args : args.url;
  if (url === '/auth/refresh') {
    endSession();
    return result;
  }

  // The pending promise is captured BEFORE it is cleared. Clearing inside
  // `.finally()` runs a microtask ahead of the queued `await`s resuming, so a
  // 401 landing in that window found `refreshInFlight` already null and
  // started a second refresh against the just-rotated token — the exact
  // stampede this shared promise exists to prevent.
  const pending = (refreshInFlight ??= refreshSession(api, extraOptions));

  let refreshed: boolean;
  try {
    refreshed = await pending;
  } finally {
    // Only the call that owns this attempt retires it; a later 401 that
    // started its own refresh must not have it cleared out from under it.
    if (refreshInFlight === pending) refreshInFlight = null;
  }

  if (!refreshed) {
    endSession();
    return result;
  }

  return rawBaseQuery(args, api, extraOptions);
};
